/**
 * <private-label-steps> — §09 "How It Works" horizontal step slider.
 * Scrolls the steps track when the prev/next nav buttons are clicked, keeps
 * the buttons enabled/disabled based on scroll position, and lets desktop
 * mouse users click-and-drag the track (touch/trackpad already scroll it
 * natively via overflow-x: auto — this only covers the mouse-drag case).
 */
class PrivateLabelSteps extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('[data-pl-track]');
    this.prevBtn = this.querySelector('[data-pl-prev]');
    this.nextBtn = this.querySelector('[data-pl-next]');
    if (!this.track) return;

    this.controller = new AbortController();
    const { signal } = this.controller;

    if (this.prevBtn) {
      this.prevBtn.addEventListener('click', () => this.scrollByCard(-1), { signal });
    }
    if (this.nextBtn) {
      this.nextBtn.addEventListener('click', () => this.scrollByCard(1), { signal });
    }
    this.track.addEventListener('scroll', () => this.updateButtons(), { signal, passive: true });
    window.addEventListener('resize', () => this.updateButtons(), { signal, passive: true });
    this.updateButtons();
    this.initDragScroll(signal);
  }

  disconnectedCallback() {
    if (this.controller) this.controller.abort();
  }

  /**
   * Lets the track be scrolled by clicking (or pen-pressing) and dragging.
   * Touch is left alone — it already scrolls natively.
   */
  initDragScroll(signal) {
    const track = this.track;
    let isDown = false;
    let dragged = false;
    let startX = 0;
    let startScrollLeft = 0;

    const endDrag = (evt) => {
      if (!isDown) return;
      isDown = false;
      track.classList.remove('is-dragging');
      if (evt && evt.pointerId !== undefined && track.hasPointerCapture(evt.pointerId)) {
        track.releasePointerCapture(evt.pointerId);
      }
    };

    track.addEventListener('pointerdown', (evt) => {
      if (evt.pointerType === 'touch') return;

      isDown = true;
      dragged = false;
      startX = evt.clientX;
      startScrollLeft = track.scrollLeft;
      track.classList.add('is-dragging');
      track.setPointerCapture(evt.pointerId);
    }, { signal });

    track.addEventListener('pointermove', (evt) => {
      if (!isDown) return;

      const delta = evt.clientX - startX;
      if (Math.abs(delta) > 3) dragged = true;
      track.scrollLeft = startScrollLeft - delta;
    }, { signal });

    track.addEventListener('pointerup', endDrag, { signal });
    track.addEventListener('pointercancel', endDrag, { signal });

    // A drag ending in a click shouldn't also fire that click (e.g. on a
    // step's own link, if one is ever added) — only swallow it once, right
    // after an actual drag.
    track.addEventListener('click', (evt) => {
      if (dragged) {
        evt.preventDefault();
        evt.stopPropagation();
        dragged = false;
      }
    }, { signal, capture: true });
  }

  scrollByCard(direction) {
    const card = this.track.querySelector('.product-private-label__step');
    const step = card ? card.getBoundingClientRect().width + 40 : this.track.clientWidth * 0.8;
    this.track.scrollBy({ left: direction * step, behavior: 'smooth' });
  }

  updateButtons() {
    if (!this.prevBtn || !this.nextBtn) return;
    const maxScroll = this.track.scrollWidth - this.track.clientWidth - 1;
    const x = this.track.scrollLeft;
    this.prevBtn.toggleAttribute('disabled', x <= 0);
    this.nextBtn.toggleAttribute('disabled', x >= maxScroll);
  }
}

if (!customElements.get('private-label-steps')) {
  customElements.define('private-label-steps', PrivateLabelSteps);
}
